// HandoverMessageField — "Message to {first name}" on the leave form.
//
// The message lives ON THE DELEGATION (delegate_note), not on the leave: the
// delegate reads it in their inbox and notification. It is loaded from the
// delegator's own delegations, edited here, and saved with
// PATCH /rbac/delegations/:id { note }. Editable only until coverage (or the
// leave) starts; after that the field explains why it is read-only.
//
// Four states, never collapsed: loading, error (with retry — an unknown note is
// not an empty one, so the field is hidden rather than offered blank and
// overwriting what is there), missing, ready.
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/apiError';
import { getDelegations, updateDelegation, type Delegation } from '@/services/rbacService';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { Button } from '@/features/shared/components/ui/button';
import { DataState } from '@/features/shared/components/ui/DataState';
import { HANDOVER_NOTE_MAX, firstName, handoverLock, type HandoverLockReason } from './handover';
import { normaliseNote } from './HandoverNote';

export type HandoverLoadState = 'idle' | 'loading' | 'error' | 'missing' | 'ready';

export interface HandoverNoteController {
  state: HandoverLoadState;
  delegation: Delegation | null;
  note: string;
  setNote: (v: string) => void;
  saved: string;
  dirty: boolean;
  saving: boolean;
  lock: HandoverLockReason;
  reload: () => void;
  /** PATCH the note. Resolves true when saved (or nothing to save). */
  save: () => Promise<boolean>;
}

export function useHandoverNote(delegationId?: string | null, leaveStart?: string | null): HandoverNoteController {
  const [state, setState] = useState<HandoverLoadState>('idle');
  const [delegation, setDelegation] = useState<Delegation | null>(null);
  const [note, setNoteRaw] = useState('');
  const [saved, setSaved] = useState('');
  const [saving, setSaving] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!delegationId) {
      setState('idle');
      setDelegation(null);
      setNoteRaw('');
      setSaved('');
      return;
    }
    let live = true;
    setState('loading');
    getDelegations()
      .then((mine) => {
        if (!live) return;
        const d = mine.asDelegator.find((x) => x.id === delegationId) ?? null;
        setDelegation(d);
        const current = normaliseNote(d?.note);
        setNoteRaw(current);
        setSaved(current);
        setState(d ? 'ready' : 'missing');
      })
      .catch(() => {
        if (live) setState('error');
      });
    return () => {
      live = false;
    };
  }, [delegationId, nonce]);

  const setNote = useCallback((v: string) => setNoteRaw(v.slice(0, HANDOVER_NOTE_MAX)), []);
  const lock = handoverLock(delegation, leaveStart);
  const dirty = state === 'ready' && normaliseNote(note) !== saved;

  const save = useCallback(async (): Promise<boolean> => {
    if (!delegationId || state !== 'ready' || lock || !dirty) return true;
    setSaving(true);
    try {
      const text = normaliseNote(note);
      const updated = await updateDelegation(delegationId, { note: text || null });
      const persisted = normaliseNote(updated?.note ?? text);
      setSaved(persisted);
      setNoteRaw(persisted);
      if (updated) setDelegation(updated);
      return true;
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save your message to the delegate'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [delegationId, state, lock, dirty, note]);

  return { state, delegation, note, setNote, saved, dirty, saving, lock, reload: () => setNonce((n) => n + 1), save };
}

export interface HandoverMessageFieldProps {
  controller: HandoverNoteController;
  delegateName?: string | null;
}

export const HandoverMessageField: React.FC<HandoverMessageFieldProps> = ({ controller: c, delegateName }) => {
  const { t } = useTranslation(['timeoff']);
  const name = firstName(delegateName) || t('timeoff:handover_the_delegate', { defaultValue: 'your delegate' });
  const label = t('timeoff:handover_label', { name, defaultValue: 'Message to {{name}}' });

  if (c.state === 'idle') return null;
  if (c.state === 'loading') {
    return <DataState variant="loading" compact message={t('timeoff:handover_loading', { defaultValue: 'Loading your handover message…' })} />;
  }
  if (c.state === 'error') {
    return (
      <DataState
        variant="error"
        compact
        message={t('timeoff:handover_load_error', { defaultValue: "Couldn't load your handover message." })}
        onRetry={c.reload}
      />
    );
  }
  if (c.state === 'missing') {
    return (
      <p className="text-xs text-muted-foreground" data-testid="handover-missing">
        {t('timeoff:handover_missing', { defaultValue: "This delegation isn't in your list any more, so a message can't be added here." })}
      </p>
    );
  }

  const locked = !!c.lock;
  const lockText =
    c.lock === 'ended'
      ? t('timeoff:handover_locked_ended', { defaultValue: 'This delegation has ended, so the message can no longer be changed.' })
      : t('timeoff:handover_locked_started', { defaultValue: 'Coverage has already started, so the message can no longer be changed.' });

  return (
    <div className="space-y-1.5" data-testid="handover-field">
      <label htmlFor="handover-note" className="block text-xs font-semibold text-muted-foreground">
        {label} <span className="font-normal">({t('timeoff:optional', { defaultValue: 'optional' })})</span>
      </label>
      <Textarea
        id="handover-note"
        value={c.note}
        onChange={(e) => c.setNote(e.target.value)}
        maxLength={HANDOVER_NOTE_MAX}
        disabled={locked || c.saving}
        rows={4}
        className="rounded-[10px] border-border text-charcoal"
        placeholder={t('timeoff:handover_placeholder', {
          defaultValue: 'What should they focus on, and where do things stand? e.g. "Please prioritise the Q3 audit approvals."',
        })}
      />
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {locked
            ? lockText
            : t('timeoff:handover_hint', { name, defaultValue: '{{name}} sees this with the delegation and in their notification. Plain text only.' })}
        </span>
        <span aria-live="polite" data-testid="handover-counter">
          {c.note.length} / {HANDOVER_NOTE_MAX}
        </span>
      </div>
      {!locked && (
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" disabled={!c.dirty || c.saving} onClick={async () => {
            if (await c.save()) toast.success(t('timeoff:handover_saved', { name, defaultValue: 'Message to {{name}} saved' }));
          }}>
            {c.saving ? t('timeoff:handover_saving', { defaultValue: 'Saving…' }) : t('timeoff:handover_save', { defaultValue: 'Save message' })}
          </Button>
          {c.dirty && <span className="text-xs text-muted-foreground">{t('timeoff:handover_unsaved', { defaultValue: 'Unsaved — it will also be saved when you submit.' })}</span>}
        </div>
      )}
    </div>
  );
};

export default HandoverMessageField;
